export const TELEMETRY_NAV_VIEWS = [
  { id: 'errors', label: 'Errors' },
  { id: 'logs', label: 'Logs' },
  { id: 'traces', label: 'Traces' },
  { id: 'queries', label: 'Queries' },
  { id: 'metrics', label: 'Metrics' },
  { id: 'profiles', label: 'Profiles' },
] as const

export type TelemetryNavView = (typeof TELEMETRY_NAV_VIEWS)[number]['id']
export type TelemetryView = TelemetryNavView | 'connect'

export const TELEMETRY_PATH_BY_VIEW: Record<TelemetryView, string> = {
  connect: 'connect',
  errors: 'errors',
  logs: 'logs',
  traces: 'traces',
  queries: 'queries',
  metrics: 'metrics',
  profiles: 'profiles',
}

/** Routed through ProjectEnvironmentTelemetry. `/logs` is the dedicated OTEL log viewer. */
export const TELEMETRY_ROUTE_SEGMENTS = [
  TELEMETRY_PATH_BY_VIEW.connect,
  TELEMETRY_PATH_BY_VIEW.errors,
  TELEMETRY_PATH_BY_VIEW.traces,
  TELEMETRY_PATH_BY_VIEW.queries,
  TELEMETRY_PATH_BY_VIEW.metrics,
  TELEMETRY_PATH_BY_VIEW.profiles,
] as const

const TELEMETRY_VIEW_BY_PATH: Record<string, TelemetryView> = {
  connect: 'connect',
  errors: 'errors',
  logs: 'logs',
  traces: 'traces',
  queries: 'queries',
  metrics: 'metrics',
  profiles: 'profiles',
}

export function parseTelemetryView(raw: string | null): TelemetryView {
  if (raw === 'connect' || raw === 'logs' || raw === 'traces' || raw === 'queries' || raw === 'metrics' || raw === 'profiles') {
    return raw
  }
  return 'errors'
}

export function parseTelemetryPathSegment(segment: string | null | undefined): TelemetryView | null {
  if (!segment) return null
  if (segment === 'otel-logs') return 'logs'
  return TELEMETRY_VIEW_BY_PATH[segment] ?? null
}

export function telemetryEnvironmentPath(
  projectId: string,
  envId: string,
  view: TelemetryView = 'errors',
  extra?: Record<string, string>,
): string {
  const path = `/project/${projectId}/environment/${envId}/${TELEMETRY_PATH_BY_VIEW[view]}`
  if (!extra || Object.keys(extra).length === 0) return path
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(extra)) {
    if (v) params.set(k, v)
  }
  const qs = params.toString()
  return qs ? `${path}?${qs}` : path
}

/** Deployment-scoped logs live on the environment OTEL logs page. */
export function deploymentTelemetryLogsPath(
  projectId: string,
  envId: string,
  deploymentId: string,
  extra?: Record<string, string>,
): string {
  return telemetryEnvironmentPath(projectId, envId, 'logs', {
    deployment: deploymentId,
    ...extra,
  })
}

export function isTelemetryEnvironmentPath(pathname: string, projectId: string, envId: string): boolean {
  return parseTelemetryViewFromPathname(pathname, projectId, envId) != null
}

export function parseTelemetryViewFromPathname(
  pathname: string,
  projectId: string,
  envId: string,
): TelemetryView | null {
  const prefix = `/project/${projectId}/environment/${envId}/`
  if (!pathname.startsWith(prefix)) return null
  const rest = pathname.slice(prefix.length).replace(/\/$/, '')
  if (!rest || rest.includes('/')) return null
  return parseTelemetryPathSegment(rest)
}

/** Old `/telemetry?view=` bookmarks. */
export function telemetryLegacySearchRedirect(projectId: string, envId: string, search: string): string {
  const params = new URLSearchParams(search)
  const view = parseTelemetryView(params.get('view'))
  params.delete('view')
  const extra: Record<string, string> = {}
  params.forEach((v, k) => {
    extra[k] = v
  })
  return telemetryEnvironmentPath(projectId, envId, view, extra)
}
